import { Box, ButtonBase, Typography } from "@mui/material";
import type React from "react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface CategoryNavItem {
  icon: React.ReactNode;
  tabTitleKey: string;
  subTabTitleKey: string;
  id: string;
  /** Optional DOM id set on the tab button, so external triggers (e.g. the
   *  What's New CTA) can open this tab by clicking it. */
  domId?: string;
}

export interface CategoryNavProps {
  items: CategoryNavItem[];
  activeId?: string | null;
  onSelect?: (id: string | null) => void;
}

export const CategoryNav = ({
  items,
  activeId,
  onSelect,
}: CategoryNavProps) => {
  const { t } = useTranslation();
  const [internalActiveId, setInternalActiveId] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);

  const currentActiveId = activeId !== undefined ? activeId : internalActiveId;

  const handleSelect = useCallback(
    (id: string) => {
      const newId = currentActiveId === id ? null : id;
      if (activeId === undefined) {
        setInternalActiveId(newId);
      }
      onSelect?.(newId);
      if (newId && navRef.current) {
        const nav = navRef.current;
        requestAnimationFrame(() => {
          nav.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    },
    [activeId, currentActiveId, onSelect],
  );

  return (
    <Box
      ref={navRef}
      component="nav"
      sx={{
        display: "flex",
        width: "100%",
        backgroundColor: "var(--alias-surface-lowest, #fff)",
        borderBottom: "1px solid var(--alias-stroke-on-white-default, #d9d9d9)",
        position: "sticky",
        top: 0,
        zIndex: 30,
      }}
    >
      {items.map((item) => {
        const isActive = currentActiveId === item.id;
        return (
          <ButtonBase
            key={item.id}
            id={item.domId}
            onClick={() => handleSelect(item.id)}
            aria-selected={isActive}
            role="tab"
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: "4px", md: "8px" },
              padding: { xs: "8px 4px", md: "16px" },
              position: "relative",
              cursor: "pointer",
              borderBottom: isActive
                ? "2px solid var(--alias-stroke-on-white-brand, #009FE4)"
                : "2px solid transparent",
              "&:hover": {
                "& .tab-icon": {
                  color: "var(--alias-text-on-white-tertiary, #4d4d4d)",
                },
                "& .tab-label": {
                  color: "var(--alias-text-on-white-tertiary, #4d4d4d)",
                },
              },
            }}
          >
            <Box
              className="tab-icon"
              sx={{
                display: "flex",
                color: isActive
                  ? "var(--alias-text-on-white-brand, #009FE4)"
                  : "var(--alias-text-on-white-secondary, #262626)",
              }}
            >
              {item.icon}
            </Box>
            <Box
              sx={{
                textAlign: { xs: "center", md: "left" },
                minWidth: 0,
              }}
            >
              <Typography
                className="tab-label"
                sx={{
                  fontSize: {
                    xs: "var(--body-b2-14-regular-size, 14px)",
                    md: "var(--title-t3-16-regular-size, 16px)",
                  },
                  fontWeight: 400,
                  color: isActive
                    ? "var(--alias-text-on-white-brand, #009FE4)"
                    : "var(--alias-text-on-white-secondary, #262626)",
                  whiteSpace: { xs: "normal", md: "nowrap" },
                  lineHeight: 1.2,
                }}
              >
                {t(item.tabTitleKey)}
              </Typography>
              <Typography
                className="tab-description"
                sx={{
                  display: { xs: "none", md: "block" },
                  fontSize: "var(--body-b3-12-regular-size, 12px)",
                  fontWeight: 400,
                  color: "var(--alias-text-on-white-tertiary, #4d4d4d)",
                  whiteSpace: "nowrap",
                  lineHeight: 1.4,
                  marginTop: "2px",
                }}
              >
                {t(item.subTabTitleKey)}
              </Typography>
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
};
